/**
 * Business Continuity Plan Questionnaire Schema
 * 
 * This schema defines the questions needed to generate a complete Business Continuity Plan
 * following FINRA Rule 4370 requirements for small introducing firms.
 */

export const bcpQuestionnaireSchema = {
  sections: [
    {
      id: "companyInfo",
      title: "Company Information",
      description: "Basic information about your firm",
      fields: [
        {
          id: "firmName",
          type: "text",
          label: "Firm Name",
          placeholder: "Enter your firm's legal name",
          required: true
        }
      ]
    },
    {
      id: "emergencyContacts",
      title: "Emergency Contact Persons",
      description: "Identify your firm's two emergency contact persons (required by FINRA)",
      helpText: "Each emergency contact person must be an associated person of the firm, and at least one emergency contact person must be a member of senior management and a registered principal of the firm.",
      fields: [
        {
          id: "contact1",
          type: "group",
          label: "Primary Emergency Contact",
          fields: [
            { id: "name", type: "text", label: "Name", required: true },
            { id: "title", type: "text", label: "Title or Relationship to Firm", required: true },
            { id: "mailing", type: "text", label: "Mailing Address", required: true },
            { id: "email", type: "email", label: "Email Address", required: true },
            { id: "phone", type: "tel", label: "Phone Number", required: true },
            { id: "fax", type: "tel", label: "Fax Number" }
          ]
        },
        {
          id: "contact2",
          type: "group",
          label: "Secondary Emergency Contact",
          fields: [
            { id: "name", type: "text", label: "Name", required: true },
            { id: "title", type: "text", label: "Title or Relationship to Firm", required: true },
            { id: "mailing", type: "text", label: "Mailing Address", required: true },
            { id: "email", type: "email", label: "Email Address", required: true },
            { id: "phone", type: "tel", label: "Phone Number", required: true },
            { id: "fax", type: "tel", label: "Fax Number" }
          ]
        },
        {
          id: "fcsResponsible",
          type: "text",
          label: "Person responsible for updating FINRA Contact System (FCS)",
          placeholder: "Name or title of responsible person",
          required: true
        }
      ]
    },
    {
      id: "firmPolicy",
      title: "Firm Policy",
      description: "Define your firm's business continuity objectives",
      fields: [
        {
          id: "policyStatement",
          type: "textarea",
          label: "Business Continuity Policy Statement",
          defaultValue: "Our BCP's primary objectives are to continue providing services to our customers, protect the health and safety of our employees, and fulfill our legal and regulatory obligations. In the event that we determine we are unable to continue our business we will assure customers prompt access to their funds and securities.",
          required: true
        },
        {
          id: "planApprover",
          type: "text",
          label: "Person responsible for approving the BCP (name and title)",
          helpText: "Must be a registered principal and member of senior management",
          required: true
        },
        {
          id: "planLocation",
          type: "text",
          label: "Where the BCP is stored/maintained",
          placeholder: "E.g., internal webpage or server in the file/folder name",
          required: true
        }
      ]
    },
    {
      id: "businessDescription",
      title: "Business Description",
      description: "Describe the types of business your firm conducts",
      fields: [
        {
          id: "businessTypes",
          type: "checkbox",
          label: "What types of securities does your firm deal with?",
          options: [
            { value: "equity", label: "Equity securities" },
            { value: "fixed", label: "Fixed income securities" },
            { value: "derivatives", label: "Derivatives" },
            { value: "privatePlacements", label: "Private placements" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherBusinessTypes",
          type: "text",
          label: "If you selected 'Other', please specify:",
          conditional: {
            field: "businessTypes",
            value: "other"
          }
        },
        {
          id: "customerTypes",
          type: "radio",
          label: "What types of customers do you serve?",
          options: [
            { value: "retail", label: "Retail" },
            { value: "both", label: "Retail and institutional" },
            { value: "institutional", label: "Institutional" }
          ],
          required: true
        },
        {
          id: "clearingFirm",
          type: "group",
          label: "Clearing Firm Information",
          fields: [
            { id: "name", type: "text", label: "Name", required: true },
            { id: "address", type: "text", label: "Address", required: true },
            { id: "phone", type: "tel", label: "Phone Number", required: true },
            { id: "email", type: "email", label: "Email Address", required: true },
            { id: "website", type: "url", label: "Website", required: true },
            { id: "primaryContact", type: "text", label: "Primary Contact Person", required: true },
            { id: "primaryContactPhone", type: "tel", label: "Primary Contact Phone", required: true },
            { id: "primaryContactEmail", type: "email", label: "Primary Contact Email", required: true },
            { id: "alternateContact", type: "text", label: "Alternate Contact Person", required: true },
            { id: "alternateContactPhone", type: "tel", label: "Alternate Contact Phone", required: true },
            { id: "alternateContactEmail", type: "email", label: "Alternate Contact Email", required: true }
          ]
        }
      ]
    },
    {
      id: "officeLocations",
      title: "Office Locations",
      description: "List all office locations and their functions",
      fields: [
        {
          id: "locations",
          type: "dynamic",
          label: "Office Locations",
          buttonText: "Add Office Location",
          template: {
            type: "group",
            fields: [
              { id: "name", type: "text", label: "Office Name/Number", required: true },
              { id: "address", type: "text", label: "Address", required: true },
              { id: "phone", type: "tel", label: "Main Phone Number", required: true },
              { id: "contact", type: "text", label: "Office Manager/Point of Contact", required: true },
              { id: "contactEmail", type: "email", label: "Contact Email", required: true },
              { id: "contactPhone", type: "tel", label: "Contact Phone", required: true },
              { 
                id: "transportation", 
                type: "radio", 
                label: "Do employees rely on public transportation?",
                options: [
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" }
                ],
                required: true
              },
              {
                id: "functions",
                type: "checkbox",
                label: "Functions performed at this location",
                options: [
                  { value: "orderTaking", label: "Order taking" },
                  { value: "orderEntry", label: "Order entry" },
                  { value: "execution", label: "Order execution" },
                  { value: "other", label: "Other" }
                ],
                required: true
              },
              { 
                id: "otherFunctions", 
                type: "text", 
                label: "Other functions (please specify)",
                conditional: {
                  field: "functions",
                  value: "other"
                }
              },
              { id: "alternateLocation", type: "text", label: "Alternate/Back-up Location", required: true }
            ]
          },
          minItems: 1
        }
      ]
    },
    {
      id: "alternateLocations",
      title: "Alternate Physical Locations of Employees",
      description: "Specify where employees will work during an SBD",
      fields: [
        {
          id: "alternateLocationStrategy",
          type: "textarea",
          label: "Describe your strategy for alternate work locations",
          defaultValue: "In the event of an SBD impacting our ability to operate in a location, staff in the affected office(s) will move to the closest of our unaffected office location(s) or our back-up location. If the alternate office location or back-up location is unavailable or infeasible, staff may temporarily work from a remote location.",
          required: true
        },
        {
          id: "remoteWorkCybersecurity",
          type: "checkbox",
          label: "Which cybersecurity measures will you implement for remote work?",
          options: [
            { value: "vpn", label: "Ensuring VPNs and remote access systems are properly patched" },
            { value: "entitlements", label: "Checking that system entitlements are current" },
            { value: "mfa", label: "Multi-factor authentication for remote system access" },
            { value: "education", label: "Regular cybersecurity education and reminders" },
            { value: "other", label: "Other measures" }
          ],
          required: true
        },
        {
          id: "otherCybersecurityMeasures",
          type: "text",
          label: "Other cybersecurity measures (please specify)",
          conditional: {
            field: "remoteWorkCybersecurity",
            value: "other"
          }
        }
      ]
    },
    {
      id: "customerAccess",
      title: "Customers' Access to Funds and Securities",
      description: "Explain how customers will access their funds and securities in the event of an SBD",
      fields: [
        {
          id: "customerAccessMethod",
          type: "checkbox",
          label: "How will customers be notified about accessing their funds and securities?",
          options: [
            { value: "website", label: "Post on website" },
            { value: "customerPortal", label: "Post on customer portal" },
            { value: "email", label: "Send email to customers" },
            { value: "phone", label: "Phone calls to customers" },
            { value: "other", label: "Other method" }
          ],
          required: true
        },
        {
          id: "otherCustomerAccessMethod",
          type: "text",
          label: "Other notification method (please specify)",
          conditional: {
            field: "customerAccessMethod",
            value: "other"
          }
        },
        {
          id: "clearingFirmCustomerContact",
          type: "text",
          label: "Clearing firm contact information for customer access",
          helpText: "This is what customers will be given if they need to contact your clearing firm directly",
          required: true
        }
      ]
    },
    {
      id: "dataBackup",
      title: "Data Back-Up and Recovery",
      description: "Describe how your firm backs up and recovers its data",
      fields: [
        {
          id: "primaryRecordsFormat",
          type: "radio",
          label: "What format are your primary/original books and records maintained in?",
          options: [
            { value: "electronic", label: "Electronic form" },
            { value: "paper", label: "Paper form" },
            { value: "both", label: "Both electronic and paper form" }
          ],
          required: true
        },
        {
          id: "primaryRecordsLocation",
          type: "text",
          label: "Where are your primary/original books and records maintained?",
          placeholder: "Enter address",
          required: true
        },
        {
          id: "primaryRecordsResponsible",
          type: "text",
          label: "Who is responsible for maintaining these records? (name, title, phone)",
          required: true
        },
        {
          id: "exclusiveDocuments",
          type: "textarea",
          label: "List document types not transmitted to your clearing firm",
          placeholder: "E.g., new account forms, etc.",
          required: true
        },
        {
          id: "backupRecordsFormat",
          type: "radio",
          label: "What format are your back-up records maintained in?",
          options: [
            { value: "electronic", label: "Electronic form" },
            { value: "paper", label: "Paper form" },
            { value: "both", label: "Both electronic and paper form" }
          ],
          required: true
        },
        {
          id: "backupRecordsLocation",
          type: "text",
          label: "Where are your back-up records maintained?",
          placeholder: "Enter address",
          required: true
        },
        {
          id: "backupRecordsResponsible",
          type: "text",
          label: "Who is responsible for maintaining back-up records? (name, title, phone)",
          required: true
        },
        {
          id: "paperBackupFrequency",
          type: "text",
          label: "How often do you back up paper records?",
          placeholder: "E.g., daily, weekly, monthly",
          conditional: {
            field: "backupRecordsFormat",
            values: ["paper", "both"]
          },
          required: true
        },
        {
          id: "paperBackupProcess",
          type: "textarea",
          label: "Describe your paper backup process",
          placeholder: "E.g., copying and physically taking paper copies to back-up site",
          conditional: {
            field: "backupRecordsFormat",
            values: ["paper", "both"]
          },
          required: true
        },
        {
          id: "electronicBackupFrequency",
          type: "text",
          label: "How often do you back up electronic records?",
          placeholder: "E.g., daily, real-time",
          conditional: {
            field: "backupRecordsFormat",
            values: ["electronic", "both"]
          },
          required: true
        },
        {
          id: "electronicBackupProcess",
          type: "textarea",
          label: "Describe your electronic backup process",
          conditional: {
            field: "backupRecordsFormat",
            values: ["electronic", "both"]
          },
          required: true
        },
        {
          id: "usesRecordkeepingService",
          type: "radio",
          label: "Do you use a third-party recordkeeping service?",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" }
          ],
          required: true
        },
        {
          id: "recordkeepingService",
          type: "group",
          label: "Recordkeeping Service Information",
          conditional: {
            field: "usesRecordkeepingService",
            value: "yes"
          },
          fields: [
            { id: "name", type: "text", label: "Service Name", required: true },
            { id: "address", type: "text", label: "Address", required: true },
            { id: "phone", type: "tel", label: "Phone Number", required: true }
          ]
        }
      ]
    },
    {
      id: "operationalRisk",
      title: "Operational Risk",
      description: "Describe how your firm will identify and address operational risks during an SBD",
      fields: [
        {
          id: "communicationMethods",
          type: "checkbox",
          label: "What alternative methods of communication will you use during an SBD?",
          options: [
            { value: "website", label: "Website" },
            { value: "phone", label: "Phone" },
            { value: "email", label: "Email" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherCommunicationMethods",
          type: "text",
          label: "Other communication methods (please specify)",
          conditional: {
            field: "communicationMethods",
            value: "other"
          }
        },
        {
          id: "customerNotification",
          type: "checkbox",
          label: "How will you notify customers about accessing their funds and securities?",
          options: [
            { value: "website", label: "Post on website" },
            { value: "portal", label: "Post on customer portal" },
            { value: "email", label: "Send email to customers" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherCustomerNotification",
          type: "text",
          label: "Other customer notification method (please specify)",
          conditional: {
            field: "customerNotification",
            value: "other"
          }
        }
      ]
    },
    {
      id: "financialCreditRisk",
      title: "Financial and Credit Risk",
      description: "Describe how your firm will assess and address financial and credit risks during an SBD",
      fields: [
        {
          id: "financialProcedures",
          type: "textarea",
          label: "Describe procedures if you cannot meet obligations or fund operations",
          placeholder: "E.g., requesting additional financing from bank or other credit sources",
          required: true
        }
      ]
    },
    {
      id: "missionCriticalSystems",
      title: "Mission Critical Systems",
      description: "Identify and describe your firm's mission critical systems",
      fields: [
        {
          id: "criticalSystems",
          type: "dynamic",
          label: "Critical Systems",
          buttonText: "Add Critical System",
          template: {
            type: "group",
            fields: [
              { id: "name", type: "text", label: "System Name", required: true },
              { id: "function", type: "text", label: "Function", required: true },
              { id: "provider", type: "text", label: "Provider", required: true },
              { id: "contactInfo", type: "text", label: "Contact Information", required: true }
            ]
          }
        },
        {
          id: "orderTakingMethods",
          type: "checkbox",
          label: "How does your firm currently receive orders from customers?",
          options: [
            { value: "phone", label: "Phone" },
            { value: "fax", label: "Fax" },
            { value: "email", label: "Email" },
            { value: "website", label: "Website" },
            { value: "inperson", label: "In-person visits" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherOrderTakingMethods",
          type: "text",
          label: "Other order taking methods (please specify)",
          conditional: {
            field: "orderTakingMethods",
            value: "other"
          }
        },
        {
          id: "customerNotificationMethods",
          type: "checkbox",
          label: "How will you inform customers of alternative methods for sending orders?",
          options: [
            { value: "phone", label: "Phone" },
            { value: "email", label: "Email" },
            { value: "website", label: "Website" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherCustomerNotificationMethods",
          type: "text",
          label: "Other customer notification methods (please specify)",
          conditional: {
            field: "customerNotificationMethods",
            value: "other"
          }
        },
        {
          id: "orderEntrySystem",
          type: "text",
          label: "What system does your firm use to place customer orders?",
          required: true
        },
        {
          id: "orderEntryBackup",
          type: "text",
          label: "Expected recovery time for order entry system during an SBD",
          required: true
        },
        {
          id: "alternateOrderEntryMethods",
          type: "textarea",
          label: "Alternative methods for order entry during an SBD",
          required: true
        },
        {
          id: "orderExecutionMethods",
          type: "textarea",
          label: "Describe how your firm executes orders and alternate methods during an SBD",
          conditional: {
            field: "doesOwnExecution",
            value: "yes"
          }
        },
        {
          id: "otherServices",
          type: "textarea",
          label: "Other services currently provided to customers and how they would be handled during an SBD",
          required: true
        },
        {
          id: "clearingFirmServices",
          type: "checkbox",
          label: "Which mission critical systems does your clearing firm provide?",
          options: [
            { value: "execution", label: "Order execution" },
            { value: "comparison", label: "Order comparison" },
            { value: "allocation", label: "Order allocation" },
            { value: "accountMaintenance", label: "Customer account maintenance" },
            { value: "accountAccess", label: "Customer account access" },
            { value: "fundDelivery", label: "Delivery of funds and securities" }
          ],
          required: true
        }
      ]
    },
    {
      id: "alternateCustomerCommunication",
      title: "Alternate Communications with Customers",
      description: "Describe alternate means to communicate with customers during an SBD",
      fields: [
        {
          id: "customerCommMethods",
          type: "checkbox",
          label: "What alternate communication methods will you use with customers?",
          options: [
            { value: "website", label: "Website" },
            { value: "phone", label: "Phone" },
            { value: "email", label: "Email" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherCustomerCommMethods",
          type: "text",
          label: "Other customer communication methods (please specify)",
          conditional: {
            field: "customerCommMethods",
            value: "other"
          }
        }
      ]
    },
    {
      id: "alternateEmployeeCommunication",
      title: "Alternate Communications with Employees",
      description: "Describe alternate means to communicate with employees during an SBD",
      fields: [
        {
          id: "employeeCommMethods",
          type: "checkbox",
          label: "What alternate communication methods will you use with employees?",
          options: [
            { value: "website", label: "Website" },
            { value: "phone", label: "Phone" },
            { value: "email", label: "Email" },
            { value: "text", label: "Text messages" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherEmployeeCommMethods",
          type: "text",
          label: "Other employee communication methods (please specify)",
          conditional: {
            field: "employeeCommMethods",
            value: "other"
          }
        },
        {
          id: "callTreeResponsible",
          type: "text",
          label: "Who is responsible for initiating the call tree?",
          required: true
        },
        {
          id: "callTree",
          type: "dynamic",
          label: "Call Tree Structure",
          buttonText: "Add Call Tree Entry",
          template: {
            type: "group",
            fields: [
              { id: "caller", type: "text", label: "Caller", required: true },
              { id: "recipients", type: "textarea", label: "Call Recipients", required: true }
            ]
          },
          minItems: 1
        }
      ]
    },
    {
      id: "regulatorCommunication",
      title: "Communications with Regulators",
      description: "Describe alternate means to communicate with regulators during an SBD",
      fields: [
        {
          id: "regulators",
          type: "dynamic",
          label: "Regulators",
          buttonText: "Add Regulator",
          template: {
            type: "group",
            fields: [
              { id: "name", type: "text", label: "Regulator Name", required: true }
            ]
          },
          minItems: 1
        },
        {
          id: "regulatorCommMethods",
          type: "checkbox",
          label: "What alternate communication methods will you use with regulators?",
          options: [
            { value: "website", label: "Website" },
            { value: "phone", label: "Phone" },
            { value: "email", label: "Email" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherRegulatorCommMethods",
          type: "text",
          label: "Other regulator communication methods (please specify)",
          conditional: {
            field: "regulatorCommMethods",
            value: "other"
          }
        }
      ]
    },
    {
      id: "businessConstituents",
      title: "Business Constituents",
      description: "Identify critical business constituents",
      fields: [
        {
          id: "criticalBusinessConstituents",
          type: "dynamic",
          label: "Critical Business Constituents",
          buttonText: "Add Business Constituent",
          template: {
            type: "group",
            fields: [
              { id: "service", type: "text", label: "Service/Product", required: true },
              { id: "supplier", type: "text", label: "Supplier's Name, Address, and Phone Number", required: true },
              { id: "alternate", type: "text", label: "Alternate Supplier's Name, Address, and Phone Number" }
            ]
          },
          minItems: 0
        }
      ]
    },
    {
      id: "banks",
      title: "Banks",
      description: "Identify banks maintaining your accounts",
      fields: [
        {
          id: "operatingBank",
          type: "group",
          label: "Operating Account Bank",
          fields: [
            { id: "name", type: "text", label: "Bank Name", required: true },
            { id: "address", type: "text", label: "Bank Address", required: true },
            { id: "phone", type: "tel", label: "Phone Number", required: true },
            { id: "contact", type: "text", label: "Contact Person", required: true }
          ]
        },
        {
          id: "paibBank",
          type: "group",
          label: "PAIB Account Bank",
          fields: [
            { id: "name", type: "text", label: "Bank Name", required: true },
            { id: "address", type: "text", label: "Bank Address", required: true },
            { id: "phone", type: "tel", label: "Phone Number", required: true },
            { id: "contact", type: "text", label: "Contact Person", required: true }
          ]
        },
        {
          id: "alternateFinancing",
          type: "group",
          label: "Alternative Financing Source",
          fields: [
            { id: "name", type: "text", label: "Bank/Lender Name", required: true },
            { id: "address", type: "text", label: "Address", required: true },
            { id: "phone", type: "tel", label: "Phone Number", required: true }
          ]
        }
      ]
    },
    {
      id: "regulatoryReporting",
      title: "Regulatory Reporting",
      description: "Describe how your firm will file regulatory reports during an SBD",
      fields: [
        {
          id: "regulatoryFilingMethods",
          type: "checkbox",
          label: "How does your firm fulfill regulatory reporting obligations?",
          options: [
            { value: "paper", label: "Paper copies in U.S. mail" },
            { value: "electronic", label: "Electronic means" },
            { value: "finraGateway", label: "FINRA Gateway" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherRegulatoryFilingMethods",
          type: "text",
          label: "Other regulatory filing methods (please specify)",
          conditional: {
            field: "regulatoryFilingMethods",
            value: "other"
          }
        },
        {
          id: "regulatoryContacts",
          type: "dynamic",
          label: "Regulatory Contacts",
          buttonText: "Add Regulatory Contact",
          template: {
            type: "group",
            fields: [
              { id: "regulator", type: "text", label: "Regulator", required: true },
              { id: "contactPerson", type: "text", label: "Contact Person", required: true },
              { id: "contactInfo", type: "text", label: "Contact Information", required: true }
            ]
          },
          minItems: 1
        }
      ]
    },
    {
      id: "customerDisclosure",
      title: "Disclosure of Business Continuity Plan to Customers",
      description: "Describe how your firm discloses its BCP to customers",
      fields: [
        {
          id: "disclosureMethods",
          type: "checkbox",
          label: "How do you disclose your BCP to customers?",
          options: [
            { value: "accountOpening", label: "At account opening" },
            { value: "website", label: "Posted on website" },
            { value: "mailedOnRequest", label: "Mailed upon request" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherDisclosureMethods",
          type: "text",
          label: "Other disclosure methods (please specify)",
          conditional: {
            field: "disclosureMethods",
            value: "other"
          }
        }
      ]
    },
    {
      id: "updating",
      title: "Updates and Annual Review",
      description: "Describe how your firm updates and reviews its BCP",
      fields: [
        {
          id: "annualReviewDate",
          type: "date",
          label: "Annual BCP review date",
          required: true
        },
        {
          id: "conductsTest",
          type: "radio",
          label: "Do you conduct a firm-wide test of your BCP?",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" }
          ],
          required: true
        }
      ]
    },
    {
      id: "seniorManagerApproval",
      title: "Senior Manager Approval",
      description: "Information about the senior manager approving the BCP",
      fields: [
        {
          id: "approverName",
          type: "text",
          label: "Name of Approver",
          required: true
        },
        {
          id: "approverTitle",
          type: "text",
          label: "Title of Approver",
          required: true
        },
        {
          id: "approvalDate",
          type: "date",
          label: "Date of Approval",
          required: true
        }
      ]
    },
    {
      id: "disclosureStatement",
      title: "BCP Disclosure Statement",
      description: "Customize your BCP disclosure statement for customers",
      fields: [
        {
          id: "firmEmergencyNumber",
          type: "tel",
          label: "Firm Emergency Phone Number",
          required: true
        },
        {
          id: "websiteURL",
          type: "url",
          label: "Firm Website URL",
          required: true
        },
        {
          id: "customerContactInfo",
          type: "text",
          label: "Customer Contact Information",
          placeholder: "Firm phone number/email for customers to contact normally",
          required: true
        },
        {
          id: "clearingFirmServices",
          type: "checkbox",
          label: "Which services can your clearing firm provide to customers during an SBD?",
          options: [
            { value: "fundAccess", label: "Prompt access to funds and securities" },
            { value: "orderEntry", label: "Enter orders" },
            { value: "tradeProcessing", label: "Process trade-related transactions" },
            { value: "cashTransfer", label: "Process cash transfers" },
            { value: "securityTransfer", label: "Process security transfers" },
            { value: "other", label: "Other" }
          ],
          required: true
        },
        {
          id: "otherClearingFirmServices",
          type: "text",
          label: "Other clearing firm services (please specify)",
          conditional: {
            field: "clearingFirmServices",
            value: "other"
          }
        },
        {
          id: "resumptionTimePeriod",
          type: "text",
          label: "Clearing firm's expected resumption time period",
          placeholder: "E.g., 4 hours, same business day",
          required: true
        },
        {
          id: "additionalDisclosureInfo",
          type: "textarea",
          label: "Additional information for customer disclosure statement",
          required: false
        }
      ]
    }
  ]
};